<template>
  <div id="app">
    <v-app>
      <Navbar 
        :isLoggedIn="isLoggedIn"
        :displayname="displayname" 
        :selectedCustomer="selectedCustomer"
        :app="{
          userProfile, 
          restApi,
          reset
        }"
        :auth="auth"
      />
      
      <v-content>
        <v-container fluid class='pa-0'>
          <v-row justify='start'>
            <v-col>
              <ProgressSpinner 
                :isLoading="!!isLoading"
                :value="progressVal"
                :linear="progressLinear"
                backgroundColor="#000"
              />
              <router-view 
                :isLoggedIn="isLoggedIn"
                :systemId="systemId"
                :app="{
                  restApi,
                  userProfile,
                  isLoading,
                  setUserProfile,
                  reset
                }"
                :setProgressVal="setProgressVal"
                :setProgressLinear="setProgressLinear"
                :auth="auth"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-content>
    </v-app>
  </div>
</template>

<script>
import axios from 'axios'
import AuthProvider from './util/AuthProvider'
import env from '../server.config'
import Navbar from './components/navbar/Navbar'
import ProgressSpinner from './components/loaders/ProgressSpinner'

export default {
  name: "App",
  components: {
    Navbar,
    ProgressSpinner,
  },
  data() {
    return {
      restApi: {},
      userProfile: {},
      selectedCustomer: {},
      isLoggedIn: false,
      isLoading: 0,
      progressVal: 0,
      progressLinear: false,
      systemId: -1,
      auth: undefined,
    }
  },
  watch: {
    userProfile(val) {
      console.log("app::watch userProfile", val)
      if (this.userProfile && Object.keys(this.userProfile).length > 5) {
        this.isLoggedIn = true
      }
    },
  },
  computed: {
    displayname() {
      return this.isLoggedIn 
        ? `${this.userProfile?.firstName} ${this.userProfile?.lastName}`
        : ''
    },
  },
  mounted() {
    // initialize firebase session and user
    this.userProfile = JSON.parse(localStorage.getItem("userProfile"));
    this.auth = new AuthProvider(this.checkFbQxSync);

    // initialize api
    this.restApi = axios.create({
      baseURL: env.apiBaseUrl,
      withCredentials: true // use session cookies
    })

    // before a request is made start the nprogress
    this.restApi.interceptors.request.use(config => {
      this.isLoading++
      return config
    })

    // before a response is returned stop nprogress
    this.restApi.interceptors.response.use(
      response => {
        if (this.isLoading > 0) {
          this.isLoading--
        }
        return response
      },
      error => {
        this.isLoading = 0
        // 401 is JWT validation error
        // 410 is PHP session invalid
        if (401 === error.response.status || 410 === error.response.status) {
          if (this.userProfile && Object.keys(this.userProfile).length > 0){
            // Only do this once - we may have made multiple API calls that will fail
            alert(`Your session has expired. You will be redirected to the login page. (${error.response.status})`);
            sessionStorage.setItem('redirectPath', this.$router.currentRoute.path);
            this.$router.push("/login/login_required");
            this.reset();
          }
          return error;
        } else {
          // this propagates error to catch() at caller
          return Promise.reject(error);
        } 
      }
    )

    // if user only has one visible customer, select it by default
    const { visibleCompanies } = this.userProfile
    if(visibleCompanies && visibleCompanies.length === 1) {
      this.setSelectedCustomer(visibleCompanies[0])
    }
  },
  methods: {
    // clear state
    reset(clearStorage = true) {
      this.userProfile = {} // this triggers watch() in app
      this.isLoggedIn = false
      this.selectedCustomer.id = -1
      this.selectedCustomer.name = ''
      this.systemId = -1
      if (clearStorage) {
        localStorage.clear();
      }
      console.log("app::reset()", this.$router.currentRoute.fullPath)
      if (!this.$router.currentRoute.fullPath.includes('login')) {
        this.$router.push('/login');
      }
    },

    checkFbQxSync() {
      /* This is my callback when Firebase auth changes state.  Here are the scenarios:
      1) firebase = undefined, qx = undefined  => no action
      2) firebase = undefined, qx = userX => sign out to kill qx PHP session
      3) firebase = userX, qx = undefined => sign in to qx and activate UI
      4) firebase = userX, qx = userX => no action
      5) firebase = userX, qx = userY => undesirable so sign out of both to clear cache
      */
      const firebaseUser = this.auth.GetCurrentUser();
      if (this.userProfile?.email && firebaseUser?.email != this.userProfile?.email) { // scenario 2 or 5
        // mismatch between QxAPI stored profile and firebase profile.  Need to reset and sign out to kill PHP session
        //console.log("App::checkFbQxSync() email mismatch ", firebaseUser?.email, this.userProfile?.email)
        this.auth.SignOut(this.userProfile, this.restApi, this.reset);
      } else if (firebaseUser?.email && !this.userProfile?.email) { // scenario 3
        //console.log("App::checkFbQxSync() got a firebase email", firebaseUser?.email, this.userProfile?.email)
        try {
          const userProfile = JSON.parse(localStorage.getItem("userProfile"));  // get the latest, may have been changed by another tab
          if (userProfile?.email) {
            this.setUserProfile(userProfile);
            this.$router.replace('/home');
          }
        } catch (err) {
          console.error("App::checkFbQxSync() json parsing error: ", err?.message);
        }
      }
    },

    setUserProfile(userProfile) {
      console.log("new user profile:", userProfile)
      localStorage.setItem('userProfile', JSON.stringify(userProfile))
      this.userProfile = userProfile
      const customer = {
        name: userProfile.companyName,
        id: userProfile.companyId
      }
      this.setSelectedCustomer(customer)
    },

    setSelectedCustomer(newCustomer) {
      this.selectedCustomer = newCustomer
    },

    setProgressVal(val) {
      this.progressVal = val
    },

    setProgressLinear(val) {
      this.progressLinear = val
    },
  }
}
</script>

<style>
.v-list-group__items .v-list-item {
  padding-left: 32px;
}

.v-application .v-list-item--link:before {
  background-color: #039af4;
}

.body-content {
  max-width: 100%;
  padding: 0px;
}
</style>